/**
 * Lightweight client-side event tracker for WTPL conversion funnel.
 *
 * Usage:
 *   import { trackEvent } from '../lib/tracker';
 *   trackEvent('pricing_view', { source: 'landing' });
 *
 * Auto-attaches `anon_id` from localStorage so anonymous → user events stitch
 * server-side at signup time.
 */
import api from './api';

const ANON_KEY = 'wtpl_anon_id';

const generateAnonId = () => {
  // RFC 4122-ish UUID v4 (no crypto dependency)
  return 'anon_xxxxxxxxxxxxxxxx'.replace(/x/g, () =>
    Math.floor(Math.random() * 16).toString(16)
  );
};

export const getAnonId = () => {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = generateAnonId();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

// In-memory dedupe — don't double-fire the same event within a session (e.g. on re-render)
const fired = new Set();

export const trackEvent = (event, metadata = {}, opts = {}) => {
  if (!event) return;
  const dedupeKey = opts.dedupeKey || event;
  if (!opts.allowDuplicate && fired.has(dedupeKey)) return;
  fired.add(dedupeKey);

  const payload = {
    event,
    anon_id: getAnonId(),
    metadata: metadata || {},
    path: typeof window !== 'undefined' ? window.location.pathname : null,
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
  };

  // Fire and forget — never block UX
  api.post('/track-event', payload).catch((e) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[tracker] failed', event, e?.message || e);
    }
  });
};

// Reset dedupe (useful on route changes when you want to refire view events)
export const resetTrackerDedupe = () => fired.clear();

// Helper: observe an element and fire `event` once it scrolls into view.
// Returns a cleanup fn.
export const trackOnVisible = (element, event, metadata = {}) => {
  if (!element || typeof IntersectionObserver === 'undefined') return () => {};
  const obs = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          trackEvent(event, metadata);
          obs.disconnect();
        }
      });
    },
    { threshold: 0.4 }
  );
  obs.observe(element);
  return () => obs.disconnect();
};
